import React, { useEffect, useMemo, useState } from "react";

const API =
  process.env.NODE_ENV === "production"
    ? "https://ecovanta.onrender.com"
    : "http://localhost:5000";

const TARGET_SCORE = 4.0;
const FREE_VISIBLE_GAPS = 2;

function getGapPriority(gap) {
  if (gap >= 2) return "Critical";
  if (gap >= 1.25) return "High";
  if (gap >= 0.75) return "Medium";
  if (gap > 0) return "Low";
  return "On Track";
}

function getGapActions(dimensionName) {
  const actions = {
    Governance: [
      "Assign ESG ownership at executive level.",
      "Create an ESG governance structure.",
      "Introduce regular board or leadership review."
    ],
    Strategy: [
      "Define ESG objectives linked to business priorities.",
      "Set measurable targets for priority ESG topics.",
      "Integrate ESG into annual planning."
    ],
    "Risk Management": [
      "Identify ESG risks across operations and value chain.",
      "Assess ESG risks by likelihood and impact.",
      "Create mitigation plans for material ESG risks."
    ],
    "Environmental Management": [
      "Measure key environmental impacts.",
      "Set reduction targets for priority environmental areas.",
      "Track environmental initiatives and performance."
    ],
    "Social Impact": [
      "Strengthen employee wellbeing monitoring.",
      "Formalize DEI policies and KPIs.",
      "Create a structured stakeholder engagement process."
    ],
    "ESG Data & Reporting": [
      "Standardize ESG data collection.",
      "Define data owners and validation controls.",
      "Align reporting with recognized ESG frameworks."
    ],
    "Technology & Automation": [
      "Move ESG tracking from spreadsheets to a structured system.",
      "Automate recurring ESG data collection workflows.",
      "Use dashboards and analytics for ESG decisions."
    ]
  };

  return (
    actions[dimensionName] || [
      "Define clear ownership.",
      "Set measurable improvement targets.",
      "Track progress regularly."
    ]
  );
}

export default function ESGGapAnalysis() {
  const [assessments, setAssessments] = useState([]);
  const [status, setStatus] = useState("Loading latest ESG assessment...");

  useEffect(() => {
    async function loadAssessments() {
      try {
        const response = await fetch(`${API}/maturity-assessments`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.error || "Unable to load assessments.");
        }

        setAssessments(Array.isArray(data) ? data : []);
        setStatus("");
      } catch (error) {
        console.error("Load gap analysis data failed:", error);
        setStatus(`Failed to load gap analysis data: ${error.message}`);
      }
    }

    loadAssessments();
  }, []);

  const latestAssessment = assessments[0];

  const gapData = useMemo(() => {
    if (!latestAssessment?.dimensionScores) return [];

    return latestAssessment.dimensionScores
      .map((dimension) => {
        const currentScore = Number(dimension.score || 0);
        const gap = Math.max(
          0,
          Number((TARGET_SCORE - currentScore).toFixed(1))
        );

        return {
          name: dimension.name,
          currentScore,
          targetScore: TARGET_SCORE,
          gap,
          priority: getGapPriority(gap),
          actions: getGapActions(dimension.name)
        };
      })
      .sort((a, b) => b.gap - a.gap);
  }, [latestAssessment]);

  const visibleGaps = gapData.slice(0, FREE_VISIBLE_GAPS);
  const lockedGapCount = Math.max(0, gapData.length - FREE_VISIBLE_GAPS);

  if (status) {
    return (
      <main
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "40px 20px"
        }}
      >
        <h1>ESG Gap Analysis</h1>
        <p>{status}</p>
      </main>
    );
  }

  if (!latestAssessment) {
    return (
      <main
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "40px 20px"
        }}
      >
        <h1>ESG Gap Analysis</h1>
        <p>No saved ESG Maturity Assessment found.</p>
        <a href="/esg-maturity-benchmark">Start ESG Maturity Benchmark</a>
      </main>
    );
  }

  return (
    <main
      style={{
        maxWidth: "1100px",
        margin: "0 auto",
        padding: "40px 20px"
      }}
    >
      <h1>ESG Gap Analysis</h1>

      <p>
        This analysis compares your latest ESG Maturity Benchmark results
        against a target maturity score of <strong>{TARGET_SCORE}/5</strong>.
      </p>

      <div
        style={{
          padding: "20px",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          background: "#f8fafc",
          marginBottom: "24px"
        }}
      >
        <h2>Latest Assessment</h2>
        <p>
          <strong>Date:</strong>{" "}
          {new Date(latestAssessment.createdAt).toLocaleDateString()}
        </p>
        <p>
          <strong>Overall Score:</strong> {latestAssessment.overallScore}/5
        </p>
        <p>
          <strong>Maturity Level:</strong> {latestAssessment.maturityLevel}
        </p>
      </div>

      <h2>Priority Gaps</h2>

      <p style={{ color: "#64748b" }}>
        Free preview: showing your top {FREE_VISIBLE_GAPS} ESG gaps. Unlock Pro
        to view all {gapData.length} dimensions and a detailed action roadmap.
      </p>

      <div style={{ display: "grid", gap: "16px" }}>
        {visibleGaps.map((gap) => (
          <div
            key={gap.name}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "18px",
              background: "#ffffff"
            }}
          >
            <h3 style={{ marginTop: 0 }}>{gap.name}</h3>

            <p>
              <strong>Current Score:</strong> {gap.currentScore}/5
            </p>

            <p>
              <strong>Target Score:</strong> {gap.targetScore}/5
            </p>

            <p>
              <strong>Gap:</strong> {gap.gap}
            </p>

            <p>
              <strong>Priority:</strong> {gap.priority}
            </p>

            <h4>Recommended Actions</h4>

            <ul>
              {gap.actions.map((action) => (
                <li key={action}>{action}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {lockedGapCount > 0 && (
        <div
          style={{
            marginTop: "30px",
            padding: "30px",
            borderRadius: "16px",
            border: "2px solid #0f766e",
            textAlign: "center",
            background: "#f8fafc"
          }}
        >
          <h2>Unlock Full ESG Gap Analysis Pro</h2>

          <p>
            You are seeing a free preview. Unlock the full analysis to access
            all ESG dimensions, detailed action plans, roadmap planning,
            benchmark insights, and export features.
          </p>

          <div
            style={{
              margin: "20px auto",
              maxWidth: "520px",
              textAlign: "left"
            }}
          >
            <ul>
              <li>All 7 ESG maturity dimensions</li>
              <li>{lockedGapCount} additional hidden gap areas</li>
              <li>Detailed recommended actions</li>
              <li>90-day ESG improvement roadmap</li>
              <li>Advanced benchmark insights</li>
              <li>PDF export for leadership and advisory use</li>
            </ul>
          </div>

          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() => {
                window.location.href = "/request-access";
              }}
              style={{
                padding: "12px 24px",
                borderRadius: "8px",
                border: "none",
                background: "#0f766e",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              Upgrade to Pro
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: "30px" }}>
        <a href="/esg-maturity-history">View Assessment History</a>
      </div>
    </main>
  );
}